// Abstraction layer for local storage operations
// This ensures all local storage access is centralized and can be easily mocked/tested

import { VisitorIdentitySnapshot } from '../types';

const STORAGE_KEYS = {
    CART: 'restaurant_cart',
    CUSTOMER: 'restaurant_customer',
    ORDER_HISTORY: 'restaurant_order_history',
    VISITOR_IDENTITY: 'restaurant_visitor_identity',
} as const;

export interface StorageService {
    getCart(): any;
    setCart(cart: any): void;
    clearCart(): void;
    getCustomer(): any;
    setCustomer(customer: any): void;
    getOrderHistory(): any[];
    addToOrderHistory(order: any): void;
    getVisitorIdentity(): VisitorIdentitySnapshot | null;
    setVisitorIdentity(identity: VisitorIdentitySnapshot): void;
}

class LocalStorageService implements StorageService {
    private get<T>(key: string): T | null {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return null;
        }
    }

    private set(key: string, value: any): void {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error('Error writing to localStorage:', error);
        }
    }

    private remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Error removing from localStorage:', error);
        }
    }

    getCart(): any {
        return this.get(STORAGE_KEYS.CART) || {
            items: [],
            totals: {
                subtotal: 0,
                discountAmount: 0,
                total: 0
            },
            discount: null,
            total: 0
        };
    }

    setCart(cart: any): void {
        this.set(STORAGE_KEYS.CART, cart);
    }

    clearCart(): void {
        this.remove(STORAGE_KEYS.CART);
    }

    getCustomer(): any {
        return this.get(STORAGE_KEYS.CUSTOMER) || null;
    }

    setCustomer(customer: any): void {
        this.set(STORAGE_KEYS.CUSTOMER, customer);
    }

    getOrderHistory(): any[] {
        return this.get(STORAGE_KEYS.ORDER_HISTORY) || [];
    }

    addToOrderHistory(order: any): void {
        const history = this.getOrderHistory();
        history.unshift(order); // Add to beginning
        // Keep only last 10 orders
        if (history.length > 10) {
            history.splice(10);
        }
        this.set(STORAGE_KEYS.ORDER_HISTORY, history);
    }

    getVisitorIdentity(): VisitorIdentitySnapshot | null {
        const stored = this.get<VisitorIdentitySnapshot | { id: string } | null>(STORAGE_KEYS.VISITOR_IDENTITY);

        if (!stored || typeof stored !== 'object') {
            return null;
        }

        const { id } = stored as { id?: unknown };
        if (typeof id !== 'string' || id.trim().length === 0) {
            return null;
        }

        const createdAt = 'createdAt' in stored && typeof (stored as { createdAt?: unknown }).createdAt === 'string'
            ? (stored as { createdAt: string }).createdAt
            : new Date().toISOString();

        const lastSeenAt = 'lastSeenAt' in stored && typeof (stored as { lastSeenAt?: unknown }).lastSeenAt === 'string'
            ? (stored as { lastSeenAt: string }).lastSeenAt
            : createdAt;

        const visitCount = 'visitCount' in stored && Number.isFinite((stored as { visitCount?: unknown }).visitCount)
            ? Number((stored as { visitCount: number }).visitCount)
            : 1;

        return {
            id,
            createdAt,
            lastSeenAt,
            visitCount
        };
    }

    setVisitorIdentity(identity: VisitorIdentitySnapshot): void {
        this.set(STORAGE_KEYS.VISITOR_IDENTITY, identity);
    }
}

// Create singleton instance
export const storageService: StorageService = new LocalStorageService();

// For testing - allows injection of mock storage
export const createStorageService = (implementation?: StorageService): StorageService => {
    return implementation || storageService;
};
